/**
 * Shared value decoders for the lenient parser, pure `.ts` with no THREE. Each falls
 * back silently when a value is absent, and warns and falls back when it is present
 * but unparseable, so a malformed scene still renders. `context` labels the warning.
 */
import { warn } from '../logger';
// These wrap the canonical leaf scanners. One-off structured literals (StyleBox shapes)
// stay in their slice.
import { parseVector2, parseVector3 } from './vectors';
import { slotTupleRegex, allFinite } from '../godot/variant/number.js';
import { slotComponents, slotInt, storedVector2i } from '../godot/variant/intSlots.js';
import { isEmptyAabb } from '../godot/math/aabb.js';
import { nodePathLiteral } from '../godot/variant/variantParser.js';
import { stringToInt } from '../godot/string/string.js';
import { boolSlotValue, scalarSlotValue } from '../godot/variant/variantBool.js';
import { storedReal, storedVector2, storedVector3 } from '../godot/math/real.js';
/**
 * A finite scalar in the tokenizer's grammar, or `null`.
 *
 * The renderer's half of the split: a non-finite is legal in the file and
 * undrawable here, so it falls back to the documented default like malformed
 * text does. `v.float` accepts it on the linter side.
 */
function finiteScalar(value) {
    const num = scalarSlotValue(value);
    return num !== null && Number.isFinite(num) ? num : null;
}
const RECT2_PATTERN = slotTupleRegex('Rect2', 4);
/**
 * `Rect2(x, y, w, h)` as a rect, or undefined when absent (silently) or malformed
 * (with a warning). Built on the canonical float grammar:
 * each component must parse whole, so `1.2.3` and `--1` are refused outright
 * instead of truncating or landing as NaN.
 */
export function parseOptionalRect2(value, context = 'value') {
    if (value === undefined)
        return undefined;
    const m = RECT2_PATTERN.exec(value);
    if (!m) {
        warn(`${context}: invalid Rect2 "${value}", treating as unset`);
        return undefined;
    }
    // A `Rect2i` spelling narrows every component to int32 before the widening
    // conversion runs, so the two spellings do not carry the same numbers.
    const c = slotComponents(value, 'Rect2', [m[1], m[2], m[3], m[4]]);
    // `1e999` is inside the finite grammar and outside what a viewport can draw.
    if (!allFinite(c)) {
        warn(`${context}: non-finite Rect2 "${value}"`);
        return undefined;
    }
    return { x: c[0], y: c[1], width: c[2], height: c[3] };
}
const AABB_PATTERN = slotTupleRegex('AABB', 6);
/** `AABB(x, y, z, w, h, d)` as a box, or undefined when absent (silently) or malformed (with a warning). */
export function parseOptionalAabb(value, context = 'value') {
    if (value === undefined)
        return undefined;
    const m = AABB_PATTERN.exec(value);
    if (!m) {
        warn(`${context}: invalid AABB "${value}", treating as unset`);
        return undefined;
    }
    const c = slotComponents(value, 'AABB', m.slice(1, 7));
    if (!allFinite(c)) {
        warn(`${context}: non-finite AABB "${value}"`);
        return undefined;
    }
    return { position: { x: c[0], y: c[1], z: c[2] }, size: { x: c[3], y: c[4], z: c[5] } };
}
/**
 * A box that an `AABB()` clears, such as `custom_aabb` (`renderer_scene_cull.cpp:1093`,
 * `mesh_storage.cpp:705-712`, `:2236`): the box, or null where it is absent, malformed or empty.
 *
 * @canonical An AABB that must have a size
 */
export function parseNonEmptyAabb(value, context) {
    const box = parseOptionalAabb(value, context);
    return box && !isEmptyAabb(box) ? box : null;
}
/**
 * A packed array slot through `parse`, or `fallback` for an unset or empty slot. An unreadable
 * slot warns and reads `fallback` too.
 *
 * @canonical A packed array slot with a fallback
 */
export function packedOr(value, parse, fallback, context = 'value') {
    if (!value)
        return fallback;
    try {
        return parse(value);
    }
    catch (error) {
        warn(`${context}: ${error instanceof Error ? error.message : String(error)}, using the default`);
        return fallback;
    }
}
export function floatOr(value, fallback, context = 'value') {
    if (value === undefined)
        return fallback;
    // Anchored, so `1.2.3` and `1abc` are refused, not read as 1.2 and 1. A non-finite
    // falls back: the documented renderer/linter split.
    const parsed = finiteScalar(value);
    if (parsed === null) {
        warn(`${context}: invalid float "${value}", using ${fallback}`);
        return fallback;
    }
    return parsed;
}
/**
 * {@link floatOr} as a `real_t` field holds it. The double is tested for finite before it is
 * stored, so `1e39` reads as the inf a `real_t` stores.
 *
 * @canonical A `real_t` field
 *   They store a double beyond the float range as inf, as `real_t` does.
 */
export function realOr(value, fallback, context = 'value') {
    return storedReal(floatOr(value, fallback, context));
}
/**
 * `width` is the setter's argument type, `'int32'` for almost every property.
 * `Camera3D`/`Decal::set_cull_mask` and `AudioStreamPlayer2D/3D::set_area_mask` take
 * `uint32_t`, where signed reads `4294967295` as -1. The validator declares the width
 * again, and `intSlotWidth.guard.test.ts` holds the two to one answer.
 */
export function intOr(value, fallback, context = 'value', width = 'int32') {
    if (value === undefined)
        return fallback;
    // The whole token parses, so `hframes = 2e1` is the 20 Godot and the linter read, and
    // `z_index = 4294967295` is the -1 Godot holds.
    const parsed = slotInt(value, null, width);
    if (parsed === null) {
        warn(`${context}: invalid int "${value}", using ${fallback}`);
        return fallback;
    }
    return parsed;
}
export function boolOr(value, fallback, context = 'value') {
    if (value === undefined)
        return fallback;
    // `boolSlotValue` reads the int and float spellings a BOOL slot converts.
    const stored = boolSlotValue(value);
    if (stored !== undefined)
        return stored;
    warn(`${context}: invalid bool "${value}", using ${fallback}`);
    return fallback;
}
/**
 * A property whose setter refuses a negative argument before assigning
 * (`sphere_shape_3d.cpp:86`, `circle_shape_2d.cpp:46`, both capsules), so it keeps its
 * default. `undefined` for absent, unparseable or negative, warning on the last two:
 * the capsule pair clamps each other only for values the setter accepted.
 */
export function settableNonNegative(value, context = 'value') {
    if (value === undefined)
        return undefined;
    const parsed = finiteScalar(value);
    if (parsed === null) {
        warn(`${context}: invalid float "${value}", keeping the Godot default`);
        return undefined;
    }
    if (parsed < 0) {
        warn(`${context}: Godot refuses the negative value "${value}", keeping the default`);
        return undefined;
    }
    return parsed;
}
/** {@link settableNonNegative} with a concrete default, the common case. */
export function nonNegativeOr(value, fallback, context = 'value') {
    return settableNonNegative(value, context) ?? fallback;
}
/**
 * An int whose setter refuses a value outside `range` before assigning (an
 * `ERR_FAIL_COND` guard), so the property keeps its default. The caller cites the guard.
 */
export function settableIntOr(value, fallback, range, context = 'value') {
    return unlessRefused(intOr(value, fallback, context), fallback, range, context);
}
/** {@link settableIntOr} for a float setter. */
export function settableFloatOr(value, fallback, range, context = 'value') {
    return unlessRefused(floatOr(value, fallback, context), fallback, range, context);
}
function unlessRefused(value, fallback, { min, max = Infinity }, context) {
    if (value >= min && value <= max)
        return value;
    warn(`${context}: Godot's setter refuses ${value}, keeping the default ${fallback}`);
    return fallback;
}
/**
 * A size whose setter refuses the whole assignment when any component is negative
 * (`box_shape_3d.cpp:100`, `rectangle_shape_2d.cpp:61`), so it keeps its default. Takes
 * a decoded vector: the grammar belongs to `parseVector2` and `parseVector3`.
 */
export function nonNegativeSizeOr(size, fallback, context = 'value') {
    if (size.x < 0 || size.y < 0 || (size.z ?? 0) < 0) {
        warn(`${context}: Godot refuses a size with a negative component, using the default`);
        return fallback;
    }
    return size;
}
/**
 * @canonical An engine enum, held as an `as const` object in `godot/`
 */
export function enumOr(value, fallback, allowed, context = 'value') {
    if (value === undefined)
        return fallback;
    // An enum constant is an `int` in every BIND_ENUM_CONSTANT.
    const parsed = slotInt(value);
    if (parsed === null || !allowed.includes(parsed)) {
        warn(`${context}: invalid enum "${value}", using ${fallback}`);
        return fallback;
    }
    return parsed;
}
/** A `Vector2(x, y)` property through the throwing `parseVector2` scanner. */
export function vec2Or(value, fallback, context = 'value') {
    if (!value)
        return fallback;
    try {
        return parseVector2(value);
    }
    catch {
        warn(`${context}: invalid Vector2 "${value}", using fallback`);
        return fallback;
    }
}
/** A `Vector3(x, y, z)` property through the throwing `parseVector3` scanner. */
export function vec3Or(value, fallback, context = 'value') {
    if (!value)
        return fallback;
    try {
        return parseVector3(value);
    }
    catch {
        warn(`${context}: invalid Vector3 "${value}", using fallback`);
        return fallback;
    }
}
/**
 * {@link vec2Or} as a `Vector2` field holds it, each component at single precision.
 *
 * @canonical A `real_t` field
 */
export function realVec2Or(value, fallback, context = 'value') {
    return storedVector2(vec2Or(value, fallback, context));
}
/**
 * {@link vec3Or} as a `Vector3` field holds it, each component at single precision.
 *
 * @canonical A `real_t` field
 */
export function realVec3Or(value, fallback, context = 'value') {
    return storedVector3(vec3Or(value, fallback, context));
}
/** The integer sibling of {@link vec2Or}: {@link parseOptionalVector2i}, or `fallback`. */
export function vec2iOr(value, fallback, context = 'value') {
    return parseOptionalVector2i(value, context) ?? fallback;
}
/**
 * The integer sibling of {@link parseOptionalVector2}, but it warns on a malformed
 * value: a malformed `frame_coords` would otherwise pick frame (0,0) with no sign.
 * `storedVector2i` reads it: the slot takes any number token and truncates it
 * (`_parse_construct<int32_t>`), so `SubViewport.size = Vector2i(2e1, 2e1)` is `(20, 20)`.
 */
export function parseOptionalVector2i(value, context = 'value') {
    if (!value)
        return undefined;
    const stored = storedVector2i(value);
    if (stored === 'malformed') {
        warn(`${context}: invalid Vector2i "${value}"`);
        return undefined;
    }
    if (stored === 'unstorable') {
        warn(`${context}: Vector2i "${value}" has a component Godot cannot store`);
        return undefined;
    }
    return stored;
}
/**
 * `undefined` for an absent or unparseable int, with no warning, where "unset" means
 * something (Control layout props). `width` is as on {@link intOr}: read at int32, a
 * `uint32_t` slot refuses `3e9`, a float literal it holds exactly.
 */
export function parseOptionalInt(value, width = 'int32') {
    if (value === undefined)
        return undefined;
    return slotInt(value, null, width) ?? undefined;
}
/**
 * Optional bool reader: `undefined` for an absent value, else what the BOOL
 * slot stores for it. An unconvertible value reads `false`, not `undefined`.
 * Distinct from `boolOr`; used where a missing property is meaningful (Control
 * flags that default off only when present, so absence stays unset).
 */
export function parseOptionalBool(value) {
    if (value === undefined)
        return undefined;
    return boolSlotValue(value) === true;
}
/**
 * `parseOptionalInt` for floats. Finite means finite as a double, so `1e39` passes, though a
 * `real_t` stores it as inf. `writtenScalarSlotValue` keeps the non-finite literals.
 */
export function parseOptionalFloat(value) {
    if (value === undefined)
        return undefined;
    return finiteScalar(value) ?? undefined;
}
/**
 * `undefined` for an absent or unparseable Vector2, with no warning, through
 * `parseVector2` and its `FLOAT_PATTERN_SOURCE`. For Control `custom_minimum_size`.
 * StyleBox `shadow_offset` needs a concrete offset, so it uses `vec2Or`.
 */
export function parseOptionalVector2(value) {
    if (!value)
        return undefined;
    try {
        return parseVector2(value);
    }
    catch {
        return undefined;
    }
}
/**
 * The path a NodePath slot stores: `NodePath("../a")` and the bare `"../a"` it converts
 * (variant.cpp:746-749) both give `"../a"`. `null` for absent or any other spelling, so
 * the caller picks a fallback. The linter's `linterUtils.extractNodePath` also rejects
 * an empty path.
 */
export function parseNodePathLiteral(value) {
    if (value === undefined)
        return null;
    return nodePathLiteral(value);
}
/**
 * The sibling index a heading's `index=` names. `resource_format_text.cpp:269-270`
 * assigns it to an `int index` (:196) through `Variant::_to_int` (`variant.h:372-373`)
 * and `String::to_int()`: `index="3px"` is 3 and `index=" "` is 0. {@link stringToInt}
 * is that model.
 */
export function parseHeadingIndex(value) {
    if (value === undefined || value === '')
        return undefined;
    return stringToInt(value);
}
//# sourceMappingURL=valueParsers.js.map